import React, { useState } from 'react';
import styled from 'styled-components';
import {
  ButtonPrimary,
  cssColorValue,
  screenSmMin,
  screenMdMin,
  semanticColors,
  spacer12,
  spacer16,
  spacer32,
  spacer40,
  TextLink,
  Type,
} from '@spotify-internal/encore-web';

import { SwitchModal } from 'client/screens/IntegrationConnectionScreen/components';
import { CoverArtPlaceholder } from 'client/components/CoverArtPlaceholder';
import { Podcast } from 'client/types';
import { useT } from 'client/i18n/hooks/useT';
import { IntegrationRedirectScreenSection } from './styles';

const IntegrationRedirectScreenConfirmationContent = styled.div`
  text-align: center;
  max-width: ${screenMdMin};
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  width: 85%;

  > p {
    margin-bottom: ${spacer16};
  }
`;

const IntegrationRedirectScreenConfirmationBlock = styled.div`
  border: 1px solid #8580a3;
  border-radius: 8px;
  width: 100%;
  padding: ${spacer32};
  display: flex;
  justify-content: space-between;
  margin-bottom: ${spacer40};

  @media (max-width: ${screenSmMin}) {
    flex-direction: column;
    padding: ${spacer16};
  }
`;

const IntegrationRedirectScreenConfirmationBlockDetails = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: ${screenSmMin}) {
    justify-content: center;
    margin-bottom: ${spacer32};
  }
`;

const IntegrationRedirectScreenConfirmationBlockDetailsImage = styled.div`
  position: relative;
  height: 64px;
  width: 64px;
  border-radius: 6px;
  overflow: hidden;
  background: ${cssColorValue(semanticColors.decorativeSubdued)};
  margin-right: ${spacer16};

  img {
    width: 100%;
    height: 100%;
  }
`;

const IntegrationRedirectScreenConfirmationBlockDetailsContext = styled.div`
  display: flex;
  flex-direction: column;
  align-items: baseline;

  h3 {
    font-size: 1.125rem;
  }

  p {
    font-size: 0.75rem;
  }
`;

export function IntegrationRedirectScreenPodcastConfirmation({
  handleSuccess,
  handleLoggedOutRedirect,
  podcastData,
  userName = '',
}: {
  handleSuccess: () => void;
  handleLoggedOutRedirect: (forceCreateAccount?: boolean) => void;
  podcastData: Podcast;
  userName?: string;
}) {
  const [showSwitchModal, setShowSwitchModal] = useState<boolean>(false);
  const t = useT();
  const {
    coverartImage: { normalSize: { url: displayImage = '' } = {} } = {},
    name: podcastName,
  } = podcastData;

  const loginSignupOption = t(
    'IntegrationRedirectScreenPodcastConfirmation.LoginSignup',
    'Not the right podcast? {signUpButton} to make a podcast or {loginButton} to another account.',
    'The text that lets the user login or sign up with a different user to connect the correct podcast to their integration.',
    {
      loginButton: (
        <TextLink
          as="button"
          key="integrationRedirectScreenLoginButton"
          onClick={() => handleLoggedOutRedirect()}
          semanticColor="textAnnouncement"
          style={{ fontWeight: 'bold', padding: 0, textTransform: 'lowercase' }}
          type="button"
        >
          {t('Button.Login', 'Log in', 'Log in button text')}
        </TextLink>
      ),
      signUpButton: (
        <TextLink
          as="button"
          key="integrationRedirectScreenSignupButton"
          onClick={() => handleLoggedOutRedirect(true)}
          semanticColor="textAnnouncement"
          style={{ fontWeight: 'bold', padding: 0 }}
          type="button"
        >
          {t('Button.Signup', 'Sign up', 'Sign up button text')}
        </TextLink>
      ),
    }
  );

  const importOption = t(
    'IntegrationConnectionScreen.LoginSignUp.Import',
    'You can also { importLink } an existing podcast to Anchor.',
    'The import message to give the option to import their whole podcast via an RSS feed. The full text in English with variables is: You can also move an existing podcast to Anchor.',
    {
      importLink: (
        <TextLink
          as="button"
          key="integrationRedirectScreenImportLink"
          onClick={() => setShowSwitchModal(true)}
          semanticColor="textAnnouncement"
          style={{ fontWeight: 'bold', padding: 0 }}
          type="button"
        >
          {t(
            'IntegrationConnectionScreen.LoginSignUp.ImportLink',
            'move',
            'The import link to navigate a user to the switch to import their whole podcast via an RSS feed'
          )}
        </TextLink>
      ),
    }
  );

  // TODO https://anchorfm.atlassian.net/browse/CAST-2750: Update to finalize text and translate
  // When we translate this, we should swap out all of the "Podcast Cover Art" strings to have
  // them be universally shared. I did not here because the launch of localization is so close,
  // I did not want to screw up translations if they should be delayed with the new ids
  return (
    <IntegrationRedirectScreenSection>
      <IntegrationRedirectScreenConfirmationContent>
        <Type as="h2" style={{ marginBottom: spacer40 }} variant="canon">
          {t(
            'IntegrationRedirectScreenPodcastConfirmation.Title',
            'Please confirm this is the podcast you want to create a new episode in',
            'The title when an integration user needs to confirm we are connecting the right data with the correct podcast'
          )}
        </Type>
        <IntegrationRedirectScreenConfirmationBlock>
          <IntegrationRedirectScreenConfirmationBlockDetails>
            <IntegrationRedirectScreenConfirmationBlockDetailsImage>
              {displayImage ? (
                <img
                  src={displayImage}
                  alt={t(
                    'SettingsScreen.EncoreSettingsForm.PodcastCoverArt.CoverArtImgAlt',
                    'Podcast cover art',
                    'Podcast cover art image screenreader information'
                  )}
                />
              ) : (
                <CoverArtPlaceholder />
              )}
            </IntegrationRedirectScreenConfirmationBlockDetailsImage>
            <IntegrationRedirectScreenConfirmationBlockDetailsContext>
              <Type as="h3" variant="balladBold">
                {podcastName}
              </Type>
              {userName && (
                <Type as="p" variant="ballad">
                  {t(
                    'EpisodeSegmentPlayer.PodcastAuthorLabel',
                    'By {podcastAuthorName}',
                    'Podcast author label',
                    { podcastAuthorName: userName }
                  )}
                </Type>
              )}
            </IntegrationRedirectScreenConfirmationBlockDetailsContext>
          </IntegrationRedirectScreenConfirmationBlockDetails>
          <ButtonPrimary onClick={handleSuccess}>
            {t('Button.Confirm', 'Confirm', 'Confirm button text')}
          </ButtonPrimary>
        </IntegrationRedirectScreenConfirmationBlock>
        <Type as="p" variant="ballad">
          {loginSignupOption}
        </Type>
        <Type as="p" variant="ballad">
          {importOption}
        </Type>
      </IntegrationRedirectScreenConfirmationContent>
      {showSwitchModal && (
        <SwitchModal dismissSwitchModal={() => setShowSwitchModal(false)} />
      )}
    </IntegrationRedirectScreenSection>
  );
}
